import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { BrandLogo, BrandLogoFull } from "@/components/BrandLogo";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-950 text-white relative overflow-hidden bwz-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandLogoFull size="md" testId="login-brand" />
            <div>
              <div className="font-display font-bold text-xl">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-0.5">OPS Central</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight">
              One control tower for every campaign.
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed">
              Plan, execute, and track installations across cities and pods. Replace fragmented spreadsheets with real-time KPIs, predictive risk alerts, and a unified calendar.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div>
                <div className="font-display text-2xl font-bold">SLA</div>
                <div className="text-xs text-zinc-500 mt-1">Real-time tracking</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold">12+</div>
                <div className="text-xs text-zinc-500 mt-1">Live campaigns</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold">8</div>
                <div className="text-xs text-zinc-500 mt-1">City pods</div>
              </div>
            </div>
          </div>
          <div className="text-xs text-zinc-500 font-mono">v1.0 • internal use</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-zinc-50">
        <Card className="w-full max-w-md p-8 border-zinc-200 shadow-sm" data-testid="login-card">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <BrandLogo to={null} size="sm" testId="login-mobile-brand" />
            <div>
              <div className="font-display font-bold">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">OPS Central</div>
            </div>
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-zinc-500 mt-1 mb-6">Use your operations credentials.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-600">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-zinc-600 hover:text-zinc-950 underline-offset-2 hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5"
              />
            </div>
            {error && <div className="text-xs text-red-600" data-testid="login-error">{error}</div>}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign in
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
